import { Mutex } from 'async-mutex';
import { Logging, LogLevel } from '#common/logging';
import { InvalidStateError } from '#common/types';
import createWasmModule from '#wasm/assembly/WasmModule';
import { parseWasmResult } from '#wasm/common';
import { AuditWasm } from './audit';
import { CompressorWasm } from './compressor';
import { ConfigWasm } from './config';
import { DataStoresWasm } from './data-stores';
import { EventsWasm } from './events';
import { FileStoreWasm } from './file-store';
import { FilespaceWasm } from './filespace';
import { FileSystemWasm } from './filesystem';
import { KeyValueHelpersWasm } from './key-value-helpers';
import { LogStoreWasm } from './log-store';
import { MetaStoreWasm } from './metastore';
import { FilePermissionsWasm } from './permissions';
import { registerSharedWasmHandlers, registerSyncWasmHandlers } from './shared-wasm-handlers';
import { SnapshotSchedulerWasm } from './snapshot-scheduler';
import { TupleWasm } from './tuple';
import { UtilsWasm } from './utils';
export var WasmLogLevel;
(function (WasmLogLevel) {
    // the values must match the LogLevel values in the C++ code
    WasmLogLevel[WasmLogLevel["LOG_DISABLED"] = 0] = "LOG_DISABLED";
    WasmLogLevel[WasmLogLevel["LOG_FATAL"] = 1] = "LOG_FATAL";
    WasmLogLevel[WasmLogLevel["LOG_ERROR"] = 3] = "LOG_ERROR";
    WasmLogLevel[WasmLogLevel["LOG_WARNING"] = 4] = "LOG_WARNING";
    WasmLogLevel[WasmLogLevel["LOG_NOTICE"] = 5] = "LOG_NOTICE";
    WasmLogLevel[WasmLogLevel["LOG_INFO"] = 6] = "LOG_INFO";
    WasmLogLevel[WasmLogLevel["LOG_DEBUG"] = 7] = "LOG_DEBUG";
    WasmLogLevel[WasmLogLevel["LOG_TRACE"] = 8] = "LOG_TRACE";
    WasmLogLevel[WasmLogLevel["LOG_ALL"] = 8] = "LOG_ALL";
})(WasmLogLevel || (WasmLogLevel = {}));
export const logLevelToWasmLogLevel = (level) => {
    switch (level) {
        case LogLevel.TRACE:
            return WasmLogLevel.LOG_TRACE;
        case LogLevel.DEBUG:
            return WasmLogLevel.LOG_DEBUG;
        case LogLevel.INFO:
            return WasmLogLevel.LOG_INFO;
        case LogLevel.WARNING:
            return WasmLogLevel.LOG_WARNING;
        case LogLevel.ERROR:
            return WasmLogLevel.LOG_ERROR;
    }
};
export const createClientWasm = async () => {
    const wasm = new ClientWasmImpl();
    await wasm.load();
    wasm.setUp();
    return wasm;
};
var ClientWasmState;
(function (ClientWasmState) {
    ClientWasmState[ClientWasmState["UNLOADED"] = 0] = "UNLOADED";
    ClientWasmState[ClientWasmState["LOADED"] = 1] = "LOADED";
    ClientWasmState[ClientWasmState["METADATA_SET_UP"] = 2] = "METADATA_SET_UP";
    ClientWasmState[ClientWasmState["FILE_SYSTEM_SET_UP"] = 3] = "FILE_SYSTEM_SET_UP";
})(ClientWasmState || (ClientWasmState = {}));
class ClientWasmImpl {
    modAsync = undefined;
    modSync = undefined;
    logger = Logging.getLogger('WASM');
    state = ClientWasmState.UNLOADED;
    fileSystemWasm = undefined;
    filespaceWasm = undefined;
    permissionsWasm = undefined;
    metaStoreWasm = undefined;
    utilsWasm = undefined;
    compressorWasm = undefined;
    keyValueHelpersWasm = undefined;
    tupleWasm = undefined;
    configWasm = undefined;
    logStoreWasm = undefined;
    fileStoreWasm = undefined;
    snapshotSchedulerWasm = undefined;
    dataStoresWasm = undefined;
    auditWasm = undefined;
    eventsWasm = undefined;
    mutex = new Mutex();
    async load() {
        // We load the wasm module twice.
        // The JS async functions are routed through the "async" wasm module.
        // The JS sync functions are routed through the "sync" wasm module.
        // This separation bypasses the Asyncify reentrancy issue
        // when we call a JS sync function and an async function is being executed and has awaited.
        this.modAsync = await createWasmModule();
        this.modSync = await createWasmModule();
        // The registered handlers are used by async functions
        registerSharedWasmHandlers(this.modAsync);
        registerSyncWasmHandlers(this.modSync);
        this.createSubModules(this.modSync, this.modAsync);
        this.state = ClientWasmState.LOADED;
    }
    // Untyped like the modSync/modAsync fields the emscripten factory returns.
    createSubModules(modSync, modAsync) {
        this.utilsWasm = new UtilsWasm(modSync);
        this.tupleWasm = new TupleWasm(modSync);
        this.configWasm = new ConfigWasm(modSync, modAsync, this.mutex);
        this.keyValueHelpersWasm = new KeyValueHelpersWasm(modSync);
        this.metaStoreWasm = new MetaStoreWasm(modAsync, this.mutex);
        this.filespaceWasm = new FilespaceWasm(modAsync, this.mutex);
        this.compressorWasm = new CompressorWasm(modSync);
        this.fileStoreWasm = new FileStoreWasm(modAsync, this.mutex);
        this.logStoreWasm = new LogStoreWasm(modAsync, this.mutex);
        this.permissionsWasm = new FilePermissionsWasm(modAsync, this.mutex);
        this.fileSystemWasm = new FileSystemWasm(modAsync, this.mutex);
        this.snapshotSchedulerWasm = new SnapshotSchedulerWasm(modAsync, this.mutex);
        this.dataStoresWasm = new DataStoresWasm(modAsync, this.mutex);
        this.auditWasm = new AuditWasm(modAsync, this.mutex);
        this.eventsWasm = new EventsWasm(modAsync, this.mutex);
    }
    setUp() {
        parseWasmResult(this.modSync.SetUpDaemon());
        parseWasmResult(this.modAsync.SetUpDaemon());
        const loggerHandler = (level, name, message) => {
            this.logger.info({ wasmLogName: name, wasmLogLevel: WasmLogLevel[level] }, message);
        };
        this.modAsync['logMessage'] = this.modSync['logMessage'] = loggerHandler;
    }
    setFilespaceGlobals(globals) {
        parseWasmResult(this.modSync.SetFilespaceGlobals(globals));
        parseWasmResult(this.modAsync.SetFilespaceGlobals(globals));
    }
    async setUpMetadataModules(nodeId, proxy) {
        this.validateWasmLoaded();
        await this.metaStoreWasm.setUp(nodeId, proxy);
        this.state = ClientWasmState.METADATA_SET_UP;
    }
    setUpFileSystemModules(getObjectHandler, principalsRsaKeysFetch) {
        this.validateMetadataSetUp();
        this.permissionsWasm.setUp(principalsRsaKeysFetch);
        this.fileSystemWasm.setUp(getObjectHandler);
        this.state = ClientWasmState.FILE_SYSTEM_SET_UP;
    }
    async setLogLevel(level, name) {
        const loggerName = name ?? '';
        parseWasmResult(this.modSync.SetLogLevel(loggerName, level));
        await this.mutex.runExclusive(() => {
            parseWasmResult(this.modAsync.SetLogLevel(loggerName, level));
        });
    }
    keyValueHelpers() {
        this.validateWasmLoaded();
        return this.keyValueHelpersWasm;
    }
    tuple() {
        this.validateWasmLoaded();
        return this.tupleWasm;
    }
    config() {
        this.validateWasmLoaded();
        return this.configWasm;
    }
    utils() {
        this.validateWasmLoaded();
        return this.utilsWasm;
    }
    compressor() {
        this.validateWasmLoaded();
        return this.compressorWasm;
    }
    metaStore() {
        this.validateMetadataSetUp();
        return this.metaStoreWasm;
    }
    filespace() {
        this.validateMetadataSetUp();
        return this.filespaceWasm;
    }
    fileSystem() {
        this.validateFileSystemSetUp();
        return this.fileSystemWasm;
    }
    permissions() {
        this.validateFileSystemSetUp();
        return this.permissionsWasm;
    }
    logStore() {
        this.validateFileSystemSetUp();
        return this.logStoreWasm;
    }
    fileStore() {
        this.validateFileSystemSetUp();
        return this.fileStoreWasm;
    }
    snapshotScheduler() {
        this.validateMetadataSetUp();
        return this.snapshotSchedulerWasm;
    }
    dataStores() {
        this.validateMetadataSetUp();
        return this.dataStoresWasm;
    }
    audit() {
        this.validateFileSystemSetUp();
        return this.auditWasm;
    }
    events() {
        this.validateMetadataSetUp();
        return this.eventsWasm;
    }
    tearDown() {
        parseWasmResult(this.modAsync.CheckForMemoryLeaks());
        parseWasmResult(this.modSync.CheckForMemoryLeaks());
        this.modAsync = undefined;
        this.modSync = undefined;
        this.fileSystemWasm = undefined;
        this.filespaceWasm = undefined;
        this.permissionsWasm = undefined;
        this.metaStoreWasm = undefined;
        this.utilsWasm = undefined;
        this.compressorWasm = undefined;
        this.keyValueHelpersWasm = undefined;
        this.tupleWasm = undefined;
        this.logStoreWasm = undefined;
        this.fileStoreWasm = undefined;
        this.auditWasm = undefined;
        this.eventsWasm = undefined;
        this.state = ClientWasmState.UNLOADED;
    }
    validateWasmLoaded() {
        if (this.state < ClientWasmState.LOADED) {
            throw new InvalidStateError('Wasm modules are not loaded! Call load() before getting any submodule!');
        }
    }
    validateMetadataSetUp() {
        if (this.state < ClientWasmState.METADATA_SET_UP) {
            throw new InvalidStateError('Wasm metadata modules are not prepared! Call setUpMetadataModules() before getting any submodule!');
        }
    }
    validateFileSystemSetUp() {
        if (this.state < ClientWasmState.FILE_SYSTEM_SET_UP) {
            throw new InvalidStateError('Wasm file system modules are not initialized! Call setUpFileSystemModules() before getting any submodule!');
        }
    }
}
